import { Autocomplete, Card, Checkbox, CircularProgress, Grid, ListItemText, MenuItem, Select, TextField } from '@mui/material'
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'
import SoftBox from 'components/SoftBox'
import SoftButton from 'components/SoftButton'
import SoftTypography from 'components/SoftTypography'
import Calendario from './components/calendario'
import { useContext, useDeferredValue, useMemo, useState } from 'react'
import { UserContext } from 'context/user'
import { gql, useQuery } from '@apollo/client'

const ESTADOS_TAREA = ["Pendiente", "En curso", "Vencida", "Incompleta", "Anulada", "Finalizada"];

const GET_USUARIOS = gql`
    query getUsuariosTareas($search: String) {
        usuarios(
            search: $search
            pagination: { page: 1, limit: 50 }
            order: { field: "apellido", order: ASC }
        ) {
            id
            nombre
            apellido
            dni
        }
    }
`;

const GET_TAGS_TAREA = gql`
    query tagsTarea($search: String) {
        tagsTarea(search: $search) {
            id
            nombre
        }
    }
`;

function formatUsuarioLabel(u) {
    if (!u) return "";
    const dni = u?.dni ? ` - DNI: ${u.dni}` : "";
    return `${u.apellido || ""}, ${u.nombre || ""}${dni}`.trim();
}

function Tareas() {
    const { user, verificarAcceso } = useContext(UserContext);
    const mode = verificarAcceso(56) || user?.tipoUser == "Administrador" ? "admin" : "user";

    const [searchUsuario, setSearchUsuario] = useState("");
    const [usuarioFiltro, setUsuarioFiltro] = useState(null);
    const [estadoFiltro, setEstadoFiltro] = useState(["Pendiente", "En curso"]);
    const [searchTag, setSearchTag] = useState("");
    const [tagFiltro, setTagFiltro] = useState(null);
    const [tipoFiltro, setTipoFiltro] = useState("");

    const { data: usuariosData, loading: loadingUsuarios } = useQuery(GET_USUARIOS, {
        variables: { search: searchUsuario || null },
        fetchPolicy: "network-only",
        skip: mode !== "admin",
    });

    const { data: tagsData, loading: loadingTags } = useQuery(GET_TAGS_TAREA, {
        variables: { search: searchTag || null },
        fetchPolicy: "network-only",
    });

    const usuariosOptions = useMemo(() => usuariosData?.usuarios || [], [usuariosData]);
    const tagsOptions = useMemo(() => tagsData?.tagsTarea || [], [tagsData]);

    const cols = useMemo(() => {
        if (mode === "admin") return { tag: 3, estado: 3, tipo: 2, limpiar: 1 };
        return { tag: 4, estado: 4, tipo: 3, limpiar: 1 };
    }, [mode]);

    const adminFilters = useMemo(() => {
        return {
            idUser: mode === "admin" ? (usuarioFiltro?.id || null) : null,
            estado: estadoFiltro.length > 0 ? estadoFiltro : null,
            idTag: tagFiltro?.id || null,
            tipoTarea: tipoFiltro || null,
        };
    }, [mode, usuarioFiltro, estadoFiltro, tagFiltro, tipoFiltro]);

    const deferredAdminFilters = useDeferredValue(adminFilters);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox py={3}>
                <SoftBox mb={3}>
                    <Card>
                        <Grid container spacing={2} p={3}>
                            <Grid
                                item
                                xs={12}
                                justifyContent="space-between"
                                display="flex"
                                sx={{
                                    flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                    },
                                }}
                            >
                                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                                    <SoftTypography variant="h6">Lista de Tareas</SoftTypography>
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    {mode === "admin" && (
                                        <Grid item xs={12} md={3}>
                                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                Filtrar por usuario
                                            </SoftTypography>
                                            <Autocomplete
                                                options={usuariosOptions}
                                                value={usuarioFiltro}
                                                loading={loadingUsuarios}
                                                onChange={(e, value) => setUsuarioFiltro(value)}
                                                getOptionLabel={formatUsuarioLabel}
                                                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                                                onInputChange={(e, value) => setSearchUsuario(value)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {loadingUsuarios ? <CircularProgress color="inherit" size={18} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} md={cols.tag}>
                                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                                            Filtrar por tag
                                        </SoftTypography>
                                        <Autocomplete
                                            options={tagsOptions}
                                            value={tagFiltro}
                                            loading={loadingTags}
                                            onChange={(e, value) => setTagFiltro(value)}
                                            getOptionLabel={(t) => t?.nombre || ""}
                                            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                                            onInputChange={(e, value) => setSearchTag(value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loadingTags ? <CircularProgress color="inherit" size={18} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={cols.estado}>
                                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                                            Filtrar por estado
                                        </SoftTypography>
                                        <Autocomplete
                                            multiple
                                            disableCloseOnSelect
                                            options={ESTADOS_TAREA}
                                            value={estadoFiltro}
                                            onChange={(e, value) => setEstadoFiltro(Array.isArray(value) ? value : [])}
                                            isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox size="small" checked={selected} />
                                                    {/* <ListItemText primary={option} /> */}
                                                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                        {option}
                                                    </SoftTypography>
                                                </li>
                                            )}
                                            renderTags={(value) => {
                                                if (!value || value.length === 0) return null;
                                                if (value.length > 2) return "Varios";
                                                return value.join(", ").trim();
                                            }}
                                            renderInput={(params) => <TextField {...params} size="small" />}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={cols.tipo}>
                                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                                            Filtrar por tipo
                                        </SoftTypography>
                                        <Select fullWidth size="small" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
                                            <MenuItem value="">Todos</MenuItem>
                                            <MenuItem value="CHECKLIST">Checklist</MenuItem>
                                            <MenuItem value="CONTROL_CAFETERIA">Control Cafetería</MenuItem>
                                        </Select>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={cols.limpiar}
                                        display="flex"
                                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                        mt={3.5}
                                    >
                                        <SoftButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() => {
                                                setUsuarioFiltro(null);
                                                setTagFiltro(null);
                                                setEstadoFiltro([]);
                                                setSearchUsuario("");
                                                setSearchTag("");
                                                setTipoFiltro("");
                                            }}
                                        >
                                            Limpiar
                                        </SoftButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Calendario mode={mode} adminFilters={deferredAdminFilters} />
                    </Card>
                </SoftBox>
            </SoftBox>
        </DashboardLayout>
    );
}

export default Tareas;
